import { createFileRoute, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { userQueryOptions } from '@/hooks/queries/users.ts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { PageContainer } from '@/components/page-container.tsx';
import { UserInfoCard } from './+components/user-info-card.tsx';
import { WorkChargingSection } from './+components/work-charging-section.tsx';
import { HomeChargingSection } from './+components/home-charging-section.tsx';
import { RoamingSection } from './+components/roaming-section.tsx';
import { ConsumptionCalculator } from './+components/consumption-calculator.tsx';

export const Route = createFileRoute('/_logged/users/$id/')({
  component: UserHubPage,
});

/**
 * The per-user hub ("Single User dashboard") — read-only, the same for everyone
 * including the signed-in admin: account info, then the At Work Charging, At
 * Home Charging, and Roaming category cards, each hosting its own gated
 * product sub-sections (RFID; NRGKick and Voltaback; ChargeMap), and the
 * consumption calculator. Every mutation UI lives on `/users/$id/edit` instead
 * (`/account/edit` for the admin's own row), reached from the header button —
 * hidden for expired users, who cannot be edited. The user query is primed by
 * the `$id` layout route's loader.
 */
function UserHubPage() {
  const { t } = useLingui();
  const { id } = Route.useParams();
  const { data: user } = useSuspenseQuery(userQueryOptions(Number(id)));
  const currentUser = useCurrentUser();
  const isSelf = user.id === currentUser.id;
  const rfidEnabled = currentUser.features.parkadmin?.feat_rfid ?? false;
  const nrgkickEnabled = currentUser.features.parkadmin?.feat_nrgkick ?? false;

  return (
    <PageContainer>
      <header className='flex flex-wrap items-start justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant='outline'
                  size='icon'
                  nativeButton={false}
                  aria-label={t`Back to users`}
                  render={<Link to='/users' />}
                >
                  <ArrowLeft />
                </Button>
              }
            />
            <TooltipContent>
              <Trans>Back to users</Trans>
            </TooltipContent>
          </Tooltip>
          <div className='space-y-1'>
            <p className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
              <Trans>User dashboard</Trans>
            </p>
            <h1 className='flex items-center gap-3 text-3xl font-semibold tracking-tight'>
              {user.username}
              {user.is_readonly ?
                <Badge variant='outline' className='text-muted-foreground'>
                  <Trans comment="Badge next to the username on the per-user dashboard when the user's park access has expired; same meaning as the 'Expired' badge in the users table.">
                    Expired
                  </Trans>
                </Badge>
              : null}
            </h1>
          </div>
        </div>
        {user.is_readonly ? null : (
          <Button
            variant='outline'
            nativeButton={false}
            render={
              isSelf ? <Link to='/account/edit' /> : <Link to='/users/$id/edit' params={{ id }} />
            }
          >
            <Pencil />
            {isSelf ?
              <Trans comment="Button on the admin's own user dashboard leading to their /account/edit page.">
                Edit account
              </Trans>
            : <Trans>Edit user</Trans>}
          </Button>
        )}
      </header>

      <div className='grid gap-6 md:grid-cols-2'>
        <UserInfoCard user={user} />
        <WorkChargingSection userId={user.id} rfidEnabled={rfidEnabled} />
        <HomeChargingSection userId={user.id} nrgkickEnabled={nrgkickEnabled} />
        <RoamingSection />
        <ConsumptionCalculator userId={user.id} />
      </div>
    </PageContainer>
  );
}
